import { useState, useCallback, useEffect, lazy, Suspense, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X } from "lucide-react";
import { useTelegram } from "@/hooks/useTelegram";
import { useTelegramCommands, TelegramCommand } from "@/hooks/useTelegramCommands";
import { useLanguage } from "@/hooks/useLanguage";
import { useAppConfig } from "@/hooks/useAppConfig";


import { useSwipe } from "@/hooks/useSwipe";

// FX Components
import ShaderBG from "@/components/effects/ShaderBG";
import Constellation from "@/components/effects/Constellation";
import GrainOverlay from "@/components/effects/GrainOverlay";
import BlobLoader from "@/components/effects/BlobLoader";
import OrganicSpinner from "@/components/effects/OrganicSpinner";
import IOSDevice from "@/components/effects/IOSDevice";
import InkWipe from "@/components/effects/InkWipe";
import CursorGlow from "@/components/effects/CursorGlow";
import { playTick } from "@/components/effects/SoundDesign";

// Screens
import HeroSection from "@/components/HeroSection";
import PhotoCards from "@/components/PhotoCards";
import ChannelCards from "@/components/ChannelCards";
import MyLinksSection from "@/components/MyLinksSection";
import ContactSection from "@/components/ContactSection";


const AboutSection = lazy(() => import("@/components/command-pages/AboutSection").then(m => ({ default: m.AboutSection })));
const VipSection = lazy(() => import("@/components/command-pages/VipSection").then(m => ({ default: m.VipSection })));
const SubscribeSection = lazy(() => import("@/components/command-pages/SubscribeSection").then(m => ({ default: m.SubscribeSection })));
const HelpSection = lazy(() => import("@/components/command-pages/HelpSection").then(m => ({ default: m.HelpSection })));
const ProfileSection = lazy(() => import("@/components/command-pages/ProfileSection").then(m => ({ default: m.ProfileSection })));
const FollowerForm = lazy(() => import("@/components/command-pages/FollowerForm").then(m => ({ default: m.FollowerForm })));
const WishlistSection = lazy(() => import("@/components/command-pages/WishlistSection").then(m => ({ default: m.WishlistSection })));
const AdminPanel = lazy(() => import("@/components/AdminPanel").then(m => ({ default: m.AdminPanel })));



const COMMAND_SLIDES: Record<TelegramCommand, number> = {
  about: 5, vip: 6, sub: 7, help: 8, profile: 9, register: 10, wishlist: 11, contact: 4, admin: 0,
};

const ease = [0.2, 0.8, 0.2, 1] as const;

// TopChrome bar (dark-only, no theme toggle)
interface TopChromeProps {
  lang: string;
  onToggleLang: () => void;
}

const TopChrome: React.FC<TopChromeProps> = ({ lang, onToggleLang }) => (
  <motion.div
    initial={{ opacity: 0, y: -10 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ delay: 0.2, duration: 0.5, ease }}
    style={{
      position: 'absolute', top: 56, left: 18, right: 18,
      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
      zIndex: 10, fontFamily: 'var(--ff-body)',
    }}
  >
    <div style={{
      display: 'flex', alignItems: 'center', gap: 8,
      fontSize: 'var(--fs-micro)', letterSpacing: '0.22em', textTransform: 'uppercase',
      color: 'var(--c-text-dim)', fontWeight: 600,
    }}>
      <span style={{ position: 'relative', width: 8, height: 8, display: 'inline-block' }}>
        <span style={{ position: 'absolute', inset: 0, borderRadius: '50%', background: 'var(--c-crimson)' }}></span>
        <span style={{ position: 'absolute', inset: 0, borderRadius: '50%', background: 'var(--c-crimson)', animation: 'pulseRing 1.8s ease-out infinite' }}></span>
      </span>
      Online
    </div>
    <button
      onClick={onToggleLang}
      aria-label={`Cambiar idioma a ${lang === 'es' ? 'English' : 'Español'}`}
      style={{
        fontSize: 'var(--fs-micro)', letterSpacing: '0.2em', color: 'var(--c-text-dim)',
        fontWeight: 600, cursor: 'pointer', background: 'none', border: 'none',
        transition: 'color 0.2s', padding: '4px',
      }}
    >
      {lang.toUpperCase()}
    </button>
  </motion.div>
);

// FAB
const FABButton: React.FC<{ menuOpen: boolean; onClick: () => void }> = ({ menuOpen, onClick }) => (
  <motion.button
    className={`fab ${menuOpen ? 'open' : ''}`}
    onClick={onClick}
    aria-label="Menú de comandos"
    whileHover={{ scale: menuOpen ? 1 : 1.06 }}
    whileTap={{ scale: 0.94 }}
  >
    <AnimatePresence mode="wait">
      {menuOpen ? (
        <motion.span key="close" initial={{ rotate: -90, opacity: 0 }} animate={{ rotate: 0, opacity: 1 }} exit={{ rotate: 90, opacity: 0 }} transition={{ duration: 0.2 }} style={{ fontSize: 22, fontWeight: 300 }}>×</motion.span>
      ) : (
        <motion.svg key="menu" initial={{ rotate: 90, opacity: 0 }} animate={{ rotate: 0, opacity: 1 }} exit={{ rotate: -90, opacity: 0 }} transition={{ duration: 0.2 }} width="22" height="22" viewBox="0 0 24 24" fill="none">
          <path d="M4 6h16M4 12h10M4 18h16" stroke="white" strokeWidth="2" strokeLinecap="round"/>
        </motion.svg>
      )}
    </AnimatePresence>
  </motion.button>
);

// Command Sheet
const CommandSheet: React.FC<{
  menuOpen: boolean;
  onClose: () => void;
  commands: Array<{ sc: string; label: string; desc: string; accent: boolean; badge?: string; command: string }>;
  onCommand: (cmd: TelegramCommand) => void;
  t: Record<string, string>;
}> = ({ menuOpen, onClose, commands, onCommand, t }) => (
  <>
    <AnimatePresence>
      {menuOpen && (
        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
          className="sheet-backdrop open" onClick={onClose}
        />
      )}
    </AnimatePresence>
    <AnimatePresence>
      {menuOpen && (
        <motion.div
          initial={{ y: '120%' }} animate={{ y: 0 }} exit={{ y: '120%' }}
          transition={{ type: 'spring' as const, stiffness: 340, damping: 28 }}
          className="sheet open rounded-t-xl"
          style={{ color: 'var(--c-text)', fontFamily: 'var(--ff-body)', transform: 'none' }}
        >
          <div style={{ padding: '14px 18px', borderBottom: '1px solid var(--c-line)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div>
              <div style={{ fontSize: 'var(--fs-micro)', letterSpacing: '0.22em', color: 'var(--c-crimson)', textTransform: 'uppercase', fontWeight: 700 }}>{t.commands_quick_title || "Comandos rápidos"}</div>
              <div style={{ fontFamily: 'var(--ff-display)', fontSize: 22, marginTop: 4, letterSpacing: '0.04em' }}>QUICK MENU</div>
            </div>
            <OrganicSpinner size={32} color="var(--c-crimson)" />
          </div>
          {commands.map((c, i) => (
            <motion.div
              key={c.sc}
              initial={{ opacity: 0, x: -12 }} animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.08 + i * 0.05, duration: 0.35, ease }}
              className={`cmd-row ${c.accent ? 'accent' : ''}`}
              onClick={() => { onClose(); onCommand(c.command as TelegramCommand); }}
              role="menuitem"
              tabIndex={0}
            >
              <div style={{ fontFamily: 'var(--ff-display)', fontSize: 14, color: c.accent ? 'var(--c-crimson)' : 'var(--c-text-dim)', letterSpacing: '0.05em' }}>{c.sc}</div>
              <div>
                <div style={{ fontSize: 14, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 8 }}>
                  {c.label}
                  {c.badge && <span style={{ background: 'var(--c-crimson)', color: '#fff', fontSize: 8, padding: '2px 6px', letterSpacing: '0.16em', fontWeight: 700 }}>{c.badge}</span>}
                </div>
                <div style={{ fontSize: 'var(--fs-caption)', color: 'var(--c-muted)', marginTop: 2 }}>{c.desc}</div>
              </div>
              <div style={{ color: 'var(--c-muted)', fontSize: 14 }}>→</div>
            </motion.div>
          ))}
          <div style={{ padding: '10px 18px', borderTop: '1px solid var(--c-line)', fontSize: 'var(--fs-micro)', color: 'var(--c-muted)', letterSpacing: '0.16em', textAlign: 'center', textTransform: 'uppercase' }}>
            {t.commands_quick_hint || "Usa los slash · en chat o aquí"}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  </>
);

// Nav rail
const NavRail: React.FC<{ total: number; activeSlide: number; labels: string[]; onGo: (i: number) => void }> = ({ total, activeSlide, labels, onGo }) => (
  <div className="nav-rail" role="navigation" aria-label="Navegación de páginas">
    {Array.from({ length: total }).map((_, i) => (
      <motion.button
        key={i}
        className={`nav-dot ${i === activeSlide ? 'active' : ''}`}
        onClick={() => onGo(i)}
        aria-label={labels[i]}
        aria-current={i === activeSlide ? 'page' : undefined}
        animate={{ width: i === activeSlide ? 28 : 18 }}
        transition={{ duration: 0.35, ease }}
        whileHover={{ scale: 1.3 }}
        whileTap={{ scale: 0.8 }}
      />
    ))}
  </div>
);

const Index = () => {
  const { hapticFeedback, isInTelegram } = useTelegram();
  const { activeCommand } = useTelegramCommands();
  const { lang, toggleLang, t } = useLanguage();
  const { config } = useAppConfig();


  // Restore slide from sessionStorage or URL deep-link
  const getInitialSlide = (): number => {
    // Check URL for deep-link: ?cmd=vip
    const params = new URLSearchParams(window.location.search);
    const cmd = params.get('cmd') as TelegramCommand | null;
    if (cmd && cmd in COMMAND_SLIDES) return COMMAND_SLIDES[cmd];
    // Check sessionStorage
    const saved = sessionStorage.getItem('activeSlide');
    if (saved) { const n = parseInt(saved, 10); if (n >= 0 && n < 12) return n; }
    return 0;
  };

  const [activeSlide, setActiveSlide] = useState(getInitialSlide);
  const [showAdmin, setShowAdmin] = useState(false);
  const [direction, setDirection] = useState(0);
  const [menuOpen, setMenuOpen] = useState(false);
  const [showSplash, setShowSplash] = useState(true);
  const [inkWipeActive, setInkWipeActive] = useState(false);
  const [inkDirection, setInkDirection] = useState<'forward' | 'backward'>('forward');
  const pendingSlide = useRef<number | null>(null);
  const phoneRef = useRef<HTMLDivElement>(null);

  const totalMainSlides = 5;
  const totalSlides = 12;
  const labels = [
    t.hero_badge || 'Inicio',
    t.gallery_title || 'Galería',
    t.channels_footer || 'Canales',
    t.mylinks_title || 'Mis redes',
    t.contact_footer || 'Contacto'
  ];
  const captions = [t.photo_caption_1, t.photo_caption_2, t.photo_caption_3, t.photo_caption_4];

  // Persist slide to sessionStorage
  useEffect(() => {
    sessionStorage.setItem('activeSlide', String(activeSlide));
  }, [activeSlide]);

  // Splash
  useEffect(() => {
    const timer = setTimeout(() => setShowSplash(false), 1100);
    return () => clearTimeout(timer);
  }, []);

  // Always dark
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', 'dark');
    document.documentElement.classList.add('dark');
  }, []);

  const goTo = useCallback((index: number) => {
    if (index < 0 || index >= totalSlides || index === activeSlide || inkWipeActive) return;

    // Trigger haptic
    hapticFeedback.impact("light");
    playTick();

    // Start ink wipe
    setInkDirection(index > activeSlide ? 'forward' : 'backward');
    setInkWipeActive(true);
    pendingSlide.current = index;

    // After wipe reaches midpoint (~55% of duration), switch slide
    setTimeout(() => {
      setDirection(index > activeSlide ? 1 : -1);
      setActiveSlide(index);
    }, 220);
  }, [activeSlide, inkWipeActive, hapticFeedback]);

  const handleInkWipeComplete = useCallback(() => {
    setInkWipeActive(false);
    pendingSlide.current = null;
  }, []);

  // Keyboard nav
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (menuOpen) { if (e.key === 'Escape') setMenuOpen(false); return; }
      if (activeSlide >= 5) return;
      if (e.key === 'ArrowRight') goTo((activeSlide + 1) % totalMainSlides);
      if (e.key === 'ArrowLeft') goTo((activeSlide - 1 + totalMainSlides) % totalMainSlides);
      if (e.code === 'Space') { e.preventDefault(); setMenuOpen(true); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [activeSlide, menuOpen, goTo]);

  // Swipe gestures
  const swipeHandlers = useSwipe({
    onSwipeLeft: () => {
      if (activeSlide < totalMainSlides - 1) goTo(activeSlide + 1);
    },
    onSwipeRight: () => {
      if (activeSlide > 0) goTo(activeSlide - 1);
    },
    enabled: !menuOpen && activeSlide < 5,
  });

  // Command handler
  const handleCommand = useCallback((command: TelegramCommand) => {
    hapticFeedback.impact("medium");
    if (command === "admin") {
      setShowAdmin(true);
      return;
    }
    const slideIndex = COMMAND_SLIDES[command];
    goTo(slideIndex);
  }, [goTo, hapticFeedback]);

  // Listen for command events
  useEffect(() => {
    const handler = (e: CustomEvent<{ command: TelegramCommand }>) => handleCommand(e.detail.command);
    window.addEventListener("telegram-command", handler as EventListener);
    return () => window.removeEventListener("telegram-command", handler as EventListener);
  }, [handleCommand]);

  useEffect(() => {
    if (activeCommand) handleCommand(activeCommand);
  }, [activeCommand, handleCommand]);

  const closeCommandPanel = () => goTo(0);
  const isCommandSlide = activeSlide >= 5;

  const commands = [
    { sc: '/about',     label: t.cmd_about_label || 'Sobre mí',   desc: t.cmd_about_desc || 'Qué puedes hacer aquí', accent: false, command: 'about' },
    { sc: '/sub',      label: t.cmd_sub_label || 'Suscribirse',desc: t.cmd_sub_desc || 'Activar suscripción',     accent: false, badge: t.badge_new || 'NUEVO', command: 'sub' },
    { sc: '/vip',      label: t.cmd_vip_label || 'VIP Plus',   desc: t.cmd_vip_desc || 'Acceso premium',          accent: true, command: 'vip' },
    { sc: '/profile',  label: t.cmd_profile_label || 'Perfil',     desc: t.cmd_profile_desc || 'Tu información',          accent: false, command: 'profile' },
    { sc: '/help',     label: t.cmd_help_label || 'Ayuda',      desc: t.cmd_help_desc || 'Soporte y FAQ',           accent: false, command: 'help' },
    { sc: '/register', label: t.cmd_register_label || 'Registrarse',desc: t.cmd_register_desc || 'Únete a la lista',        accent: false, command: 'register' },
    { sc: '/wishlist', label: t.cmd_wishlist_label || 'Wishlist',   desc: t.cmd_wishlist_desc || 'Lista de deseos',          accent: false, command: 'wishlist' },
  ];

  const screens = [
    <HeroSection key="hero" t={t} active={activeSlide === 0} onCTA={() => goTo(2)} onSkipTour={() => goTo(4)} />,
    <PhotoCards key="photos" t={t} captions={captions} active={activeSlide === 1} />,
    <ChannelCards key="channels" t={t} active={activeSlide === 2} />,
    <MyLinksSection key="mylinks" t={t} active={activeSlide === 3} />,
    <ContactSection key="contact" t={t} title={t.contact_title} subtitle={t.contact_subtitle} telegram={config.contact.telegram || config.profile.telegramUsername || config.bot.botUsername || ""} chatButton={t.contact_chat_button} active={activeSlide === 4} />,
    <Suspense key="about-sec" fallback={<div className="h-full w-full flex items-center justify-center"><OrganicSpinner size={40} /></div>}><AboutSection t={t} /></Suspense>,
    <Suspense key="vip-sec" fallback={<div className="h-full w-full flex items-center justify-center"><OrganicSpinner size={40} /></div>}><VipSection t={t} /></Suspense>,
    <Suspense key="sub-sec" fallback={<div className="h-full w-full flex items-center justify-center"><OrganicSpinner size={40} /></div>}><SubscribeSection t={t} lang={lang} /></Suspense>,
    <Suspense key="help-sec" fallback={<div className="h-full w-full flex items-center justify-center"><OrganicSpinner size={40} /></div>}><HelpSection t={t} /></Suspense>,
    <Suspense key="profile-sec" fallback={<div className="h-full w-full flex items-center justify-center"><OrganicSpinner size={40} /></div>}><ProfileSection t={t} /></Suspense>,
    <Suspense key="register-sec" fallback={<div className="h-full w-full flex items-center justify-center"><OrganicSpinner size={40} /></div>}><FollowerForm t={t} /></Suspense>,
    <Suspense key="wishlist-sec" fallback={<div className="h-full w-full flex items-center justify-center"><OrganicSpinner size={40} /></div>}><WishlistSection t={t} /></Suspense>,
  ];

  const renderScreenStack = () => (
    <div
      className="screen-stack relative w-full h-full"
      {...swipeHandlers}
      style={{ touchAction: 'pan-y', background: '#000000' }}
    >
      {/* Shader inside phone */}
      <ShaderBG intensity={1.4} style={{ position: 'absolute', inset: 0, zIndex: 0 }} />
      <Constellation activeSlide={showAdmin ? 12 : activeSlide} />

      {/* Screens */}
      <AnimatePresence initial={false} custom={direction} mode="popLayout">
        <motion.div
          key={activeSlide}
          custom={direction}
          variants={{
            enter: (d: number) => ({ x: d > 0 ? '100%' : '-100%', opacity: 0 }),
            center: { x: 0, opacity: 1 },
            exit: (d: number) => ({ x: d < 0 ? '100%' : '-100%', opacity: 0 }),
          }}
          initial="enter"
          animate="center"
          exit="exit"
          transition={{
            x: { type: 'spring' as const, stiffness: 300, damping: 35 },
            opacity: { duration: 0.35 },
          }}
          className="absolute inset-0"
          style={{ pointerEvents: 'auto', zIndex: 2 }}
        >
          {screens[activeSlide]}
        </motion.div>
      </AnimatePresence>

      {/* Ink Wipe Overlay */}
      <AnimatePresence>
        {inkWipeActive && (
          <InkWipe
            direction={inkDirection}
            onComplete={handleInkWipeComplete}
          />
        )}
      </AnimatePresence>

      {/* Cursor Glow */}
      <CursorGlow />

      {/* TopChrome */}
      {!isCommandSlide && (
        <TopChrome lang={lang} onToggleLang={toggleLang} />
      )}

      {/* Grain */}
      <GrainOverlay opacity={0.06} />

      {/* Close button for command slides */}
      {isCommandSlide && (
        <motion.button
          initial={{ opacity: 0, scale: 0.8 }} animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.2, duration: 0.3 }}
          onClick={closeCommandPanel}
          whileHover={{ scale: 1.1, backgroundColor: 'rgba(255,255,255,0.1)' }}
          whileTap={{ scale: 0.9 }}
          aria-label="Cerrar"
          className="absolute top-14 right-5 z-50 w-9 h-9 rounded-full bg-white/5 border border-white/10 flex items-center justify-center"
          style={{ color: 'var(--c-text)' }}
        >
          <X className="w-4 h-4" />
        </motion.button>
      )}

      {/* FAB */}
      {!isCommandSlide && <FABButton menuOpen={menuOpen} onClick={() => setMenuOpen(!menuOpen)} />}

      {/* Command Sheet */}
      <CommandSheet menuOpen={menuOpen} onClose={() => setMenuOpen(false)} commands={commands} onCommand={handleCommand} t={t} />

      {/* Nav Rail */}
      {!isCommandSlide && <NavRail total={totalMainSlides} activeSlide={activeSlide} labels={labels} onGo={goTo} />}
    </div>
  );

  return (
    <>
      {/* Skip link (a11y) */}
      <a href="#main-content" className="skip-link">{lang === 'es' ? 'Ir al contenido' : 'Skip to content'}</a>

      {/* Splash */}
      <AnimatePresence>
        {showSplash && (
          <motion.div initial={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.8 }}
            className="fixed inset-0 z-[200] flex flex-col items-center justify-center gap-6"
            style={{ background: 'var(--c-bg)' }}
          >
            <BlobLoader size={130} color="var(--c-crimson)" />
            <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3, duration: 0.5 }}
              style={{ fontFamily: 'var(--ff-body)', fontSize: 'var(--fs-micro)', letterSpacing: '0.4em', textTransform: 'uppercase', color: 'var(--c-crimson)', fontWeight: 600 }}
            >
              {t.loading_text || "Cargando · Mini App"}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* WebGL Background */}
      <ShaderBG intensity={1.4} />
      <Constellation activeSlide={showAdmin ? 12 : activeSlide} />
      {/* Static fallback for reduced motion */}
      <div className="no-shader-bg" style={{ display: 'none' }} />

      {/* Stage */}
      {isInTelegram ? (
        <div id="main-content" className="fixed inset-0 overflow-hidden w-full h-full z-10">
          {renderScreenStack()}
        </div>
      ) : (
        <div id="main-content" className="grid grid-cols-1 xl:grid-cols-[1fr_auto_1fr] items-center fixed inset-0 overflow-hidden w-full h-full z-10">

          {/* Left sidebar */}
          <motion.aside
            initial={{ opacity: 0, x: -30 }} animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 1.2, duration: 0.8, ease }}
            className="hidden xl:block relative z-10 p-10 max-w-[420px] justify-self-end"
            style={{ fontFamily: 'var(--ff-body)', color: 'var(--c-text-dim)' }}
          >
            <div style={{ fontSize: 'var(--fs-micro)', letterSpacing: '0.22em', textTransform: 'uppercase', color: 'var(--c-crimson)', fontWeight: 700 }}>
              Telegram Mini App / Redesign
            </div>
            <h3 style={{ fontFamily: 'var(--ff-display)', fontSize: 'clamp(40px, 5vw, 56px)', lineHeight: 0.9, margin: '12px 0 18px 0', letterSpacing: '0.01em', color: 'var(--c-text)', textTransform: 'uppercase' }}>
              {t.sidebar_left_title_1 || "Aquí y"}<br/><span style={{ fontFamily: 'var(--ff-italic)', fontStyle: 'italic', fontSize: 'clamp(36px, 4.5vw, 50px)', color: 'var(--c-crimson)', letterSpacing: '-0.01em', textTransform: 'none' }}>{t.sidebar_left_title_2 || "ahora."}</span>
            </h3>
            <p style={{ fontSize: 14, lineHeight: 1.6, color: '#b8b8b8' }}>
              {t.sidebar_left_desc || "Reescritura visual de App3 — shader fluid wallpaper, streaming text, organic loader y transiciones con motion. Toda la fuerza del original, con disciplina editorial."}
            </p>
            <div style={{ marginTop: 28, display: 'flex', gap: 8, fontSize: 11, letterSpacing: '0.06em', color: 'var(--c-muted)' }}>
              <span className="kbd" style={{ display: 'inline-flex', alignItems: 'center', gap: 6, border: '1px solid var(--c-line)', padding: '6px 10px', borderRadius: 4, color: 'var(--c-text)', fontWeight: 600, background: 'rgba(255,255,255,0.02)' }}>← →</span>
              <span style={{ alignSelf: 'center' }}>{t.sidebar_left_nav || "navegar"}</span>
            </div>
          </motion.aside>

          {/* Phone */}
          <motion.main
            initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 1.0, duration: 0.8, ease }}
            className="relative z-10 flex items-center justify-center scale-[0.9] sm:scale-95 xl:scale-100 origin-center justify-self-center"
            ref={phoneRef}
          >
            <IOSDevice dark>
              {renderScreenStack()}
            </IOSDevice>
          </motion.main>

          {/* Right sidebar */}
          <motion.aside
            initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 1.2, duration: 0.8, ease }}
            className="hidden xl:block relative z-10 p-10 max-w-[420px] justify-self-start text-right"
            style={{ fontFamily: 'var(--ff-body)', color: 'var(--c-text-dim)' }}
          >
            <div style={{ fontSize: 'var(--fs-micro)', letterSpacing: '0.22em', textTransform: 'uppercase', color: 'var(--c-crimson)', fontWeight: 700 }}>{t.sidebar_right_tag || "Sistema"}</div>
            <h3 style={{ fontFamily: 'var(--ff-display)', fontSize: 'clamp(40px, 5vw, 56px)', lineHeight: 0.9, margin: '12px 0 18px 0', letterSpacing: '0.01em', color: 'var(--c-text)', textTransform: 'uppercase' }}>
              {t.sidebar_right_title_1 || "Crimson"}<br/><span style={{ fontFamily: 'var(--ff-italic)', fontStyle: 'italic', fontSize: 'clamp(36px, 4.5vw, 50px)', color: 'var(--c-crimson)', letterSpacing: '-0.01em', textTransform: 'none' }}>{t.sidebar_right_title_2 || "discipline."}</span>
            </h3>
            <p style={{ fontSize: 14, lineHeight: 1.6, color: '#b8b8b8', marginLeft: 'auto' }}>
              {t.sidebar_right_desc || "WebGL shader → carbón profundo + plumas crimson. Anton ✕ Instrument Serif ✕ Space Grotesk. Listas con hairlines, números tabulares, micro-loader orgánico."}
            </p>
            <div style={{ marginTop: 28, display: 'flex', justifyContent: 'flex-end', gap: 8, fontSize: 11, letterSpacing: '0.06em', color: 'var(--c-muted)' }}>
              {['FAB', lang === 'es' ? '/comandos' : '/commands', 'SPACE'].map(k => (
                <span key={k} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, border: '1px solid var(--c-line)', padding: '6px 10px', borderRadius: 4, color: 'var(--c-text)', fontWeight: 600, background: 'rgba(255,255,255,0.02)' }}>{k}</span>
              ))}
            </div>
          </motion.aside>
        </div>
      )}

      {!isInTelegram && (
        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.5, duration: 0.6 }}
          className="fixed bottom-[22px] left-0 right-0 text-center z-30 pointer-events-none"
          style={{ fontSize: 'var(--fs-micro)', color: 'var(--c-muted)', letterSpacing: '0.2em', textTransform: 'uppercase' }}
        >
          {t.version_text || "v4.5 · impactante · 4 ago 2026"}
        </motion.div>
      )}

      {/* Admin Panel Access (botón invisible en esquina superior izquierda) */}
      <button
        onClick={() => {
          hapticFeedback.impact("light");
          setShowAdmin(true);
        }}
        className="fixed top-14 left-4 z-40 w-12 h-12 opacity-0 hover:opacity-10 cursor-pointer"
        aria-label="Admin"
      >
        <span className="sr-only">Admin</span>
      </button>

      {/* Admin Panel */}
      <Suspense fallback={null}>
        <AdminPanel isOpen={showAdmin} onClose={() => setShowAdmin(false)} />
      </Suspense>
    </>
  );
};

export default Index;
